import type { OrderCostPlan, OrderCostPlanLine, OrderWarehouseSummary } from '../types/orderWarehouseSummary';
import { reportDtoAnomaly } from './common';

const isAmount = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const isNullableAmount = (value: unknown) => value === null || isAmount(value);
const isNullableText = (value: unknown) => value === null || typeof value === 'string';
const PLAN_SOURCES = ['FIFO', 'RESERVED', 'PINNED'];
const PLAN_PENDING_REASONS = ['NO_AVERAGE_COST', 'AVERAGE_UNIT_UNRESOLVED', 'UNIT_UNRESOLVED', 'NO_PRODUCT', 'BATCH_COST_UNKNOWN',
  'AVERAGE_RATE_UNAVAILABLE'];
const isOptionalAmount = (value: unknown) => value === undefined || isNullableAmount(value);
const isOptionalText = (value: unknown) => value === undefined || isNullableText(value);
const PLAN_LINE_WARNINGS = ['RESERVATION_EXCEEDS_BATCH', 'CLOSED_BY_INVOICE'];

function isValidPlanLine(line: OrderCostPlanLine): boolean {
  return !!line && typeof line.id === 'string' && typeof line.costPending === 'boolean'
    && isAmount(line.issuedCostPLN) && isAmount(line.fifoCostPLN) && Array.isArray(line.fifo)
    && line.fifo.every(part => !!part && PLAN_SOURCES.includes(part.source) && isAmount(part.quantity) && isAmount(part.costPLN)
      && [part.batchNumber, part.pzNumber, part.receiptDate, part.unit].every(isNullableText))
    && [line.averageQuantity, line.averagePurchasePricePLN, line.averageCostPLN, line.pendingQuantity].every(isNullableAmount)
    && [line.unit, line.averageUnit].every(isNullableText)
    && [line.averagePurchasePrice, line.averageExchangeRate].every(isOptionalAmount)
    && [line.averagePurchaseCurrency, line.averageExchangeRateDate].every(isOptionalText)
    && (line.averagePurchasePriceUnit === null || ['pcs', 'm2', 'm3'].includes(line.averagePurchasePriceUnit))
    && (line.pendingReason === null || PLAN_PENDING_REASONS.includes(line.pendingReason))
    && Array.isArray(line.warnings) && line.warnings.every(warning => PLAN_LINE_WARNINGS.includes(warning));
}

/** Unknown cost stays null; a missing amount is a contract error, not zero. */
function isValidPlan(plan: OrderCostPlan): boolean {
  return (['participates', 'closedByInvoice', 'usesAverage', 'costPending'] as const).every(key => typeof plan[key] === 'boolean')
    && [plan.issuedCostPLN, plan.stockCostPLN, plan.averageCostPLN, plan.manualCostPLN, plan.knownCostPLN].every(isAmount)
    && [plan.totalCostPLN, plan.marginPLN, plan.marginPercent].every(isNullableAmount)
    && isNullableText(plan.confirmedAt)
    && Array.isArray(plan.lines) && plan.lines.every(isValidPlanLine);
}

/** A failed or redacted read must never look like zero stock or zero cost. */
export function normalizeOrderWarehouseSummary(raw: unknown): OrderWarehouseSummary {
  const value = raw as OrderWarehouseSummary | null;
  const invalid = () => {
    reportDtoAnomaly('OrderWarehouseSummary', 'payload', raw);
    throw new Error('Invalid warehouse summary');
  };
  if (!value || !Array.isArray(value.lines) || !Array.isArray(value.documents)) return invalid();
  if (value.lines.some(line => !line || typeof line.id !== 'string' || typeof line.name !== 'string'
    || typeof line.unit !== 'string'
    || !isAmount(line.orderedQuantity)
    || ![line.availableQuantity, line.issuedQuantity, line.remainingQuantity, line.invoicedQuantity].every(amount => amount === null || isAmount(amount)))) return invalid();
  if (value.documents.some(document => !document || typeof document.id !== 'string'
    || typeof document.wzNumber !== 'string' || typeof document.status !== 'string')) return invalid();
  if (value.finance != null && (![value.finance.knownFifoCostPLN, value.finance.transportCostPLN, value.finance.otherCostPLN].every(isAmount)
    || typeof value.finance.costPending !== 'boolean')) return invalid();
  const plan = value.finance?.plan;
  if (plan != null && !isValidPlan(plan)) return invalid();
  return {
    lines: value.lines,
    documents: value.documents,
    ...(value.finance != null ? { finance: value.finance } : {}),
  };
}
